import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

export const MessagesModal = ({ open, onOpenChange, businessId, businessName }) => {
  const [messages, setMessages] = useState([]);
  const [myBizId, setMyBizId] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const [conv, mine] = await Promise.all([
        api.get(`/messages/${businessId}`),
        api.get("/businesses/me"),
      ]);
      setMessages(conv.data);
      setMyBizId(mine.data?.id);
    } catch (e) { console.error("Failed to load conversation", e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setLoading(true); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${businessId}`, { content: text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="messages-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold flex items-center gap-2"><MessageCircle size={18} className="text-brand" /> {businessName}</DialogTitle>
        </DialogHeader>
        <div className="h-72 overflow-y-auto space-y-2 py-2 px-1">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" size={22} /></div>
          ) : messages.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-10">No messages yet. Say hello 👋</p>
          ) : messages.map((m) => {
            const mine = m.from_business_id === myBizId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-brand text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"}`}>{m.content}</div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2 pt-2 border-t border-slate-100">
          <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="bg-slate-50 h-10" />
          <Button data-testid="message-send" type="submit" disabled={sending} className="h-10 px-3 bg-brand hover:bg-brand-hover shrink-0">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
